'use client';

import React, {createContext, useContext, useState, useEffect, ReactNode, useCallback} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useClpCourse} from '../clp-course-provider.react-component';
import {useLecturesQuery} from '@/generated/graphql';
import {PreviewableLecture} from '@/types/lecture';

export interface CoursePreviewAssetData {
    /** All previewable video lectures found in the course curriculum */
    previewableLectures?: PreviewableLecture[];
    /** Loading state */
    isLoading: boolean;
    /** Error state */
    error?: string;
    /** Refresh asset data for a specific lecture */
    refreshAsset: (lectureId: string) => Promise<void>;
}

const LecturePreviewAssetContext = createContext<CoursePreviewAssetData | null>(null);

interface LecturePreviewAssetProviderProps {
    children: ReactNode;
}

/**
 * Provider that finds all previewable video lectures from the course curriculum
 * and fetches their asset data for video playback
 */
export function LecturePreviewAssetProvider({children}: LecturePreviewAssetProviderProps) {
    const course = useClpCourse();
    const queryClient = useQueryClient();
    const [previewableLectureIds, setPreviewableLectureIds] = useState<string[]>([]);
    const [lectureMap, setLectureMap] = useState<Map<string, PreviewableLecture>>(new Map());
    const [error, setError] = useState<string>();

    // Find all previewable video lectures from the curriculum
    useEffect(() => {
        if (!course?.curriculum?.sections) {
            return;
        }

        try {
            const ids: string[] = [];
            for (const section of course.curriculum.sections) {
                for (const item of section.items) {
                    // Check if it's a video lecture with isPreviewable = true
                    // We check for properties that indicate it's a video lecture rather than __typename
                    if (
                        'isPreviewable' in item &&
                        item.isPreviewable &&
                        'durationInSeconds' in item &&
                        typeof item.durationInSeconds === 'number' &&
                        'id' in item &&
                        typeof item.id === 'string'
                    ) {
                        ids.push(item.id);
                    }
                }
            }
            setPreviewableLectureIds(ids);
        } catch (err) {
            setError('Failed to find previewable lectures');
            console.error('Error finding previewable lectures:', err);
        }
    }, [course?.curriculum?.sections]);

    // Fetch lecture details when we have previewable lectures
    const {
        data: lectureData,
        isLoading,
        error: queryError,
    } = useLecturesQuery(
        {ids: previewableLectureIds},
        {
            enabled: previewableLectureIds.length > 0,
        },
    );

    // Update the lecture map when data changes
    useEffect(() => {
        if (lectureData?.lectures) {
            const newMap = new Map(lectureMap);

            lectureData.lectures.forEach((lecture) => {
                if (
                    lecture != null &&
                    lecture.__typename === 'VideoLecture' &&
                    'asset' in lecture &&
                    !!lecture.asset &&
                    lecture.asset.__typename === 'AssetVideo'
                ) {
                    // Create a PreviewableLecture with only the required fields
                    const previewableLecture: PreviewableLecture = {
                        id: lecture.id,
                        title: lecture.title,
                        durationInSeconds: lecture.durationInSeconds,
                        thumbnail: lecture.thumbnail,
                        asset: lecture.asset,
                    };
                    newMap.set(lecture.id, previewableLecture);
                }
            });

            setLectureMap(newMap);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lectureData?.lectures]);

    // Create ordered array from map based on previewableLectureIds
    const previewableLectures: PreviewableLecture[] | undefined =
        previewableLectureIds.length > 0
            ? previewableLectureIds
                  .map((id) => lectureMap.get(id))
                  .filter((lecture): lecture is PreviewableLecture => lecture != null)
            : undefined;

    // Refresh asset for a specific lecture
    const refreshAsset = useCallback(
        async (lectureId: string) => {
            try {
                // Use the fetcher directly to get fresh data for the specific lecture
                const result = await useLecturesQuery.fetcher({ids: [lectureId]})();

                if (result?.lectures) {
                    const newMap = new Map(lectureMap);

                    result.lectures.forEach((lecture) => {
                        if (
                            lecture != null &&
                            lecture.__typename === 'VideoLecture' &&
                            'asset' in lecture &&
                            !!lecture.asset &&
                            lecture.asset.__typename === 'AssetVideo'
                        ) {
                            // Create a PreviewableLecture with only the required fields
                            const previewableLecture: PreviewableLecture = {
                                id: lecture.id,
                                title: lecture.title,
                                durationInSeconds: lecture.durationInSeconds,
                                thumbnail: lecture.thumbnail,
                                asset: lecture.asset,
                            };
                            newMap.set(lecture.id, previewableLecture);
                        }
                    });

                    setLectureMap(newMap);

                    // Invalidate the main query to ensure consistency
                    queryClient.invalidateQueries({
                        queryKey: useLecturesQuery.getKey({ids: previewableLectureIds}),
                    });
                }
            } catch (err) {
                console.error('Error refreshing lecture asset:', err);
                throw err;
            }
        },
        [lectureMap, queryClient, previewableLectureIds],
    );

    const contextValue: CoursePreviewAssetData = {
        previewableLectures,
        isLoading,
        error: error || (queryError ? String(queryError) : undefined),
        refreshAsset,
    };

    return (
        <LecturePreviewAssetContext.Provider value={contextValue}>
            {children}
        </LecturePreviewAssetContext.Provider>
    );
}

/**
 * Hook to access lecture preview asset data
 */
export function useLecturePreviewAssets(): CoursePreviewAssetData {
    const context = useContext(LecturePreviewAssetContext);
    if (!context) {
        throw new Error(
            'useLecturePreviewAssets must be used within a LecturePreviewAssetProvider',
        );
    }
    return context;
}

/**
 * Hook to refresh asset data for a specific lecture
 */
export function useRefreshAsset(): (lectureId: string) => Promise<void> {
    const {refreshAsset} = useLecturePreviewAssets();
    return refreshAsset;
}
